import { ChangeEvent, FC, useState } from 'react';

import { Navigate } from 'react-router-dom';
import styled from 'styled-components';

import { Button, debounce } from '@mui/material';

import { useGetAllOrganizations } from '../../../api-http/organizations';
import { AlertBar } from '../../../components/AlertBar';
import { PageTitle } from '../../../components/PageTitle';
import { useAuth } from '../../../context/auth-context';
import { UserRole } from '../../../types/user.types';
import { metaTitle } from '../../../util/tabTitle';
import { OrganizationsTable } from './OrganizationsTable';
import { AddOrganizationModal } from './components/AddOrganizationModal';
import { AddUserModal } from '../users/components/AddUserModal';
import { StyleTextfield } from '../../../components/TextField';
import { DEBOUNCE_TIME } from '../../../configs';
import Breadcrumbs from '../../../components/Breadcrumb';
import { useHandleSearch } from '../../../util/useHandleSearch';
import { useLocationSearch } from '../../../util/useLocationSearch';

const TopContainer = styled('div')`
  margin-bottom: 20px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const OrganizationPage: FC = () => {
  metaTitle('Super Admin - Organizations');

  const searchVal = useLocationSearch();
  const { currentUser } = useAuth();
  const { error, refetch } = useGetAllOrganizations();

  const [openUserModal, setOpenUserModal] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState(searchVal || '');
  const [openCreateOrgModal, setOpenCreateOrgModal] = useState(false);
  const [search, setSearch] = useState(searchVal || '');

  if (currentUser && currentUser.role !== UserRole.superAdmin) {
    return <Navigate to="/" />;
  }
  const selectedOrg = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSelectedOrgName(event.target.value);
  };
  useHandleSearch(search);

  return (
    <>
      <PageTitle>Organizations Page (Super Admin)</PageTitle>
      <Breadcrumbs
        items={[{ label: 'Organizations', to: '/admin/organizations' }]}
      />
      <TopContainer>
        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenCreateOrgModal(true)}
            style={{ marginRight: 10 }}
          >
            Create organization
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenUserModal(true)}
            disabled={currentUser?.role === 'user'}
          >
            Add user
          </Button>
        </div>
        <StyleTextfield
          label="Search Organization"
          variant="outlined"
          onChange={(event) => {
            setSearch(event.target.value);
            debounce(() => selectedOrg(event), DEBOUNCE_TIME)();
          }}
          value={search}
        />
      </TopContainer>

      <AlertBar severity="error" msg={error?.msg} />

      <OrganizationsTable selectedOrgName={selectedOrgName} />

      <AddOrganizationModal
        onClose={() => setOpenCreateOrgModal(false)}
        open={openCreateOrgModal}
        onFinished={() => refetch()}
      />

      <AddUserModal
        onClose={() => setOpenUserModal(false)}
        open={openUserModal}
        onFinished={() => refetch()}
      />
    </>
  );
};

export default OrganizationPage;
